import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { schedulesVerificationService } from '@/services/schedulesVerification.service';
import { isSoftVerificationFailure } from '@/utils/scheduleVerification';
import type { SchedulesVerificationSummaryGlobal } from '@/types/api';

export function useSchedulesVerificationGlobal(enabled = true) {
  const queryClient = useQueryClient();

  const summaryQuery = useQuery({
    queryKey: ['schedules-verification-summary'],
    queryFn: async (): Promise<
      | { status: 'ready'; data: SchedulesVerificationSummaryGlobal }
      | { status: 'unavailable'; message: string }
    > => {
      const res = await schedulesVerificationService.getGlobalSummary();
      if (!res.success || !res.data) {
        const code = res.error?.code;
        const message =
          res.error?.message || 'Resumen de verificación no disponible';
        if (isSoftVerificationFailure(code, message)) {
          return { status: 'unavailable', message };
        }
        throw new Error(message);
      }
      return { status: 'ready', data: res.data };
    },
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });

  const verifyAllMutation = useMutation({
    mutationFn: async () => {
      const res = await schedulesVerificationService.verifyAll();
      if (!res.success) {
        const code = res.error?.code;
        const message = res.error?.message || 'Error al verificar todas las controladoras';
        if (isSoftVerificationFailure(code, message)) {
          throw new Error(
            'Verificación masiva no disponible. Compruebe que el backend v0.7 esté desplegado.'
          );
        }
        throw new Error(message);
      }
      return res.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['schedules-verification-summary'] });
      await queryClient.invalidateQueries({ queryKey: ['schedule-verification'] });
    },
  });

  const refresh = useCallback(async () => {
    await summaryQuery.refetch();
  }, [summaryQuery]);

  const availability = summaryQuery.data ?? null;

  return {
    summary: availability?.status === 'ready' ? availability.data : null,
    unavailable: availability?.status === 'unavailable' ? availability.message : null,
    isLoading: summaryQuery.isLoading,
    isVerifyingAll: verifyAllMutation.isPending,
    error:
      summaryQuery.error instanceof Error
        ? summaryQuery.error.message
        : verifyAllMutation.error instanceof Error
          ? verifyAllMutation.error.message
          : null,
    verifyAll: () => verifyAllMutation.mutateAsync(),
    refresh,
  };
}
